import { Button, ButtonRow, Heading, Text } from '@ksyos/design-system';
import { useNavigate } from '@ksyos/router';
import { useEffect, useState } from 'react';
import type { IPractitioner } from '../../interfaces/practitioner';
import { NewLoginView } from '../../../../common/enums/newLoginView';
import { TokenType } from '../../../../common/enums/tokenType';
import { TwoFactorAuthenticationType } from '../../../../common/enums/twoFactorAuthenticationType';
import { ValidationErrorCode } from '../../../../common/enums/validationErrorCode';
import { buildName } from '../../../../common/utils/buildName';
import { fetchJson } from '../../../store/fetch';
import { ChooseMfaMethod } from './ChooseMfaMethod';
import { DownloadMfaApp } from './DownloadMfaApp';
import { SaveAccountActivation } from './SaveAccountActivation';
import { ScanMfaQrCode } from './ScanMfaQrCode';
import { SetHardwareToken } from './SetHardwareToken';
import { SetPassword } from './SetPassword';
import { SuccessfulActivationPage } from './SuccessfulActivationPage';
import { TermsOfUse } from './TermsOfUse';

interface IActivateAccountProps {
    activationToken: string;
}

export interface IActivationPractitioner extends IPractitioner {
    userName: string;
}

export function ActivateAccount(props: IActivateAccountProps) {
    const [view, setView] = useState<NewLoginView>(NewLoginView.termsOfUse);
    const [practitioner, setPractitioner] = useState<IActivationPractitioner | null>(null);
    const [displayName, setDisplayName] = useState('');
    const [mfaType, setMfaType] = useState<TwoFactorAuthenticationType | undefined>();
    const [password, setPassword] = useState<string | undefined>();

    const navigate = useNavigate();
    useEffect(() => {
        loadToken();
    }, []);

    if (view === NewLoginView.alreadyActive) {
        return (
            <>
                <Heading>Je account is al geactiveerd</Heading>
                <Text>Je hebt je account al geactiveerd. Log in om aan de slag te gaan met Ksyos.</Text>
                <ButtonRow>
                    <Button
                        color="secondary"
                        onClick={() => navigate('/')}
                        width="full"
                    >
                        Inloggen
                    </Button>
                </ButtonRow>
            </>
        );
    } else if (view === NewLoginView.expiredToken) {
        return (
            <>
                <Heading>Activatielink niet meer geldig</Heading>
                <Text>
                    Je kunt deze link niet meer gebruiken. Vraag opnieuw een activatielink aan om je account te
                    activeren. Bel daarvoor de Ksyos servicedesk op <b>+31 (0)20 600 00 60</b>.
                </Text>
            </>
        );
    } else if (view === NewLoginView.chooseMfaMethod) {
        return (
            <ChooseMfaMethod
                goBack={() => setView(NewLoginView.createPassword)}
                goToNextStep={() => {
                    mfaType === TwoFactorAuthenticationType.verificationApp
                        ? setView(NewLoginView.downloadMfaApp)
                        : setView(NewLoginView.setHardwareToken);
                }}
                mfaType={mfaType}
                onChangeMfaType={(twoFactorAuthenticationType: TwoFactorAuthenticationType) => {
                    setMfaType(twoFactorAuthenticationType);
                }}
                skipStep={() => {
                    setMfaType(undefined);
                    setView(NewLoginView.activateAccount);
                }}
            />
        );
    } else if (view === NewLoginView.downloadMfaApp) {
        return (
            <DownloadMfaApp
                activationToken={props.activationToken}
                goBack={() => setView(NewLoginView.chooseMfaMethod)}
                goToNextStep={() => setView(NewLoginView.scanMfaQrCode)}
            />
        );
    } else if (view === NewLoginView.scanMfaQrCode) {
        return (
            <ScanMfaQrCode
                activationToken={props.activationToken}
                goBack={() => setView(NewLoginView.downloadMfaApp)}
                goToNextStep={() => setView(NewLoginView.activateAccount)}
                password={password}
                userName={practitioner?.userName}
            />
        );
    } else if (view === NewLoginView.setHardwareToken) {
        return (
            <SetHardwareToken
                goBack={() => setView(NewLoginView.chooseMfaMethod)}
                goToNextStep={() => setView(NewLoginView.activateAccount)}
                isActivationFlow
                onSuccess={(success: boolean) => success && setView(NewLoginView.activateAccount)}
                password={password}
                token={props.activationToken}
                userName={practitioner?.userName}
            />
        );
    } else if (view === NewLoginView.activateAccount) {
        return (
            <SaveAccountActivation
                activationToken={props.activationToken}
                goToNextStep={() => setView(NewLoginView.successPage)}
            />
        );
    } else if (view === NewLoginView.successPage) {
        return <SuccessfulActivationPage />;
    } else if (view === NewLoginView.createPassword) {
        return (
            <SetPassword
                activationToken={props.activationToken}
                goBack={() => setView(NewLoginView.termsOfUse)}
                goToNextStep={(newPassword: string) => {
                    setPassword(newPassword);
                    setView(NewLoginView.chooseMfaMethod);
                }}
                practitionerDisplayName={displayName}
                redirectPath={`/home?view=chooseMfaMethod&token=${props.activationToken}`}
                title="Account activeren"
                userName={practitioner?.userName}
            />
        );
    } else {
        return (
            <TermsOfUse
                goToNextStep={() => setView(NewLoginView.createPassword)}
                userName={displayName}
            />
        );
    }

    async function loadToken() {
        const result = await fetchJson('api/activationTokens/' + props.activationToken, 'GET', undefined, [
            ValidationErrorCode.ACTIVATION_TOKEN_EXPIRED,
            ValidationErrorCode.ACTIVATION_TOKEN_ALREADY_CONSUMED,
        ]);
        if (result.errorCode || ![TokenType.activation, TokenType.initialActivation].includes(result.json?.type)) {
            return setView(NewLoginView.expiredToken);
        } else if (result.json?.practitioner) {
            setPractitioner(result.json.practitioner);
            if (result.json.practitioner.isActivated) {
                return setView(NewLoginView.alreadyActive);
            }
            setDisplayName(
                buildName(
                    result.json.practitioner?.nameGiven,
                    result.json.practitioner?.nameInfix,
                    result.json.practitioner?.nameFamily
                )
            );
        }
    }
}
